/* global debounce */

if (!customElements.get('quick-order-list')) {
  class QuickOrderList extends HTMLElement {
    constructor() {
      super();
      
      // Initialize free product validation
      this.initFreeProductValidation();
      
      this.init();
    }

    init() {
      this.fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      };

      this.cartDrawer = document.getElementById('cart-drawer');
      this.itemStatus = document.getElementById('qol-line-item-status');
      this.currentItemCount = Array.from(this.querySelectorAll('[name="updates[]"]'))
        .reduce((total, quantityInput) => total + parseInt(quantityInput.value, 10), 0);

      this.addEventListener('click', this.handleClick.bind(this));
      this.addEventListener('change', debounce(this.handleChange.bind(this)));
    }

    initFreeProductValidation() {
      // Get free product variant ID from global theme settings
      const variantIdString = theme.settings.cartProgressBar?.freeProductVariantId;
      this.freeProductVariantId = variantIdString ? parseInt(variantIdString, 10) : null;
      
      // Only proceed if we have a free product configured
      if (!this.freeProductVariantId || this.freeProductVariantId === 0) {
        console.log('Quick Order List: No free product configured, skipping validation');
        return;
      }
      
      console.log('Quick Order List: Initializing free product validation for variant ID:', this.freeProductVariantId);
    }

    /**
     * Improved removal function using line item keys
     * @param {number} variantId - The variant ID to remove
     * @returns {Promise<Object>} Updated cart data
     */
    async removeAllByVariantId(variantId) {
      console.log('Quick Order List: Removing all items with variant ID:', variantId);
      
      try {
        // 1) Get current cart
        const cart = await fetch('/cart.js', { credentials: 'same-origin' }).then(r => r.json());
        console.log('Quick Order List: Current cart items:', cart.items.length);

        // 2) Find all line item keys for this variant
        const keysToRemove = cart.items
          .filter(item => item.variant_id === variantId)
          .map(item => item.key); // key uniquely identifies the line (variant + properties + selling plan, etc.)

        console.log('Quick Order List: Found items to remove:', keysToRemove.length);
        
        if (keysToRemove.length === 0) {
          console.log('Quick Order List: No items found to remove');
          return cart; // nothing to remove
        }

        // 3) Remove each matching line by key
        for (const key of keysToRemove) {
          console.log('Quick Order List: Removing line key:', key);
          
          const res = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: key, quantity: 0 }) // using line-item key ensures exact match
          });
          
          if (!res.ok) {
            const errorText = await res.text();
            throw new Error(`Failed to remove line key ${key}: ${errorText}`);
          }
          
          console.log('Quick Order List: Successfully removed line key:', key);
        }

        // 4) Return final cart
        const finalCart = await fetch('/cart.js', { credentials: 'same-origin' }).then(r => r.json());
        console.log('Quick Order List: Final cart items:', finalCart.items.length);
        
        return finalCart;
        
      } catch (error) {
        console.error('Quick Order List: Error removing items by variant ID:', error);
        throw error;
      }
    }

    /**
     * Calculate cart total excluding free product
     * @param {Object} cartData - Cart data
     * @returns {number} Total in cents excluding free product
     */
    calculateCartTotalExcludingFreeProduct(cartData) {
      return cartData.items
        .filter(item => {
          const isFreeProduct = item.variant_id === this.freeProductVariantId;
          const isMilestonePopupProduct = item.properties && item.properties._3rd_milestone_popup === 'true';
          return !isFreeProduct && !isMilestonePopupProduct;
        })
        .reduce((total, item) => total + (item.final_line_price), 0);
    }

    /**
     * Add free product to cart
     * @returns {Promise<Object>} Updated cart data
     */
    async addFreeProductToCart() {
      console.log('Quick Order List: Adding free product to cart');
      
      try {
        // CRITICAL: Check if free product is already in cart before adding
        console.log('Quick Order List: Performing final atomic check before adding free product');
        const freshCartCheck = await fetch(`${theme.routes.cart}.js`);
        if (!freshCartCheck.ok) {
          console.error('Quick Order List: Failed to fetch cart for final check, aborting addition');
          throw new Error('Failed to check cart status');
        }
        
        let freshCartData = await freshCartCheck.json();
        const existingFreeProductItem = freshCartData.items.find(item => 
          item.variant_id.toString() === this.freeProductVariantId.toString()
        );
        
        if (existingFreeProductItem) {
          // Check if it's already a freebie (has freebie properties)
          const isAlreadyFreebie = existingFreeProductItem.properties && 
            (existingFreeProductItem.properties._free_product === 'true' || 
             existingFreeProductItem.properties['_free_product'] === 'true');
          
          if (isAlreadyFreebie) {
            console.log('Quick Order List: ✅ FREE PRODUCT ALREADY EXISTS AS FREEBIE in cart! Skipping addition to prevent duplicate.');
            return freshCartData;
          } else {
            // Variant exists but NOT as a freebie - remove it first, then add as freebie
            console.log('Quick Order List: Variant exists as normal product (not freebie). Removing it first, then adding as freebie.');
            console.log('Quick Order List: Existing item properties:', existingFreeProductItem.properties);
            const updatedCart = await this.removeAllByVariantId(this.freeProductVariantId);
            // Continue with the addition below
          }
        }
        
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: this.freeProductVariantId,
            quantity: 1,
            properties: {
              '_free_product': 'true',
              '_rule_id': 'SPEND_X_0',
              '_kite_promo_name': 'Freebie 1'
            }
          })
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to add free product: ${errorText}`);
        }
        
        const result = await response.json();
        console.log('Quick Order List: Free product added successfully');
        return result;
        
      } catch (error) {
        console.error('Quick Order List: Error adding free product:', error);
        throw error;
      }
    }

    /**
     * Handle free product milestone logic
     * @param {Object} cartData - Current cart data
     */
    async handleFreeProductMilestone(cartData) {
      console.log('Quick Order List: ===== HANDLING FREE PRODUCT MILESTONE =====');
      console.log('Quick Order List: Cart data:', cartData);
      
      // Calculate cart total excluding free product
      const cartTotalExcludingFreeProduct = this.calculateCartTotalExcludingFreeProduct(cartData);
      console.log('Quick Order List: Cart total excluding free product:', cartTotalExcludingFreeProduct, 'cents = $', (cartTotalExcludingFreeProduct / 100));
      
      // Check if free product is currently in cart
      const freeProductItem = cartData.items.find(item => item.variant_id === this.freeProductVariantId);
      const hasFreeProduct = !!freeProductItem;
      
      console.log('Quick Order List: Has free product in cart:', hasFreeProduct);
      if (freeProductItem) {
        console.log('Quick Order List: Free product details:', freeProductItem);
      }
      
      // Get milestone 2 threshold from global settings (in dollars, convert to cents)
      const milestone2Threshold = (theme.settings.cartProgressBar?.milestone2 || 10) * 100; // Default $10
      
      console.log('🎯 Quick Order List: ===== FREEBIE THRESHOLDS =====');
      console.log('🎯 Quick Order List: Milestone 2 (Freebie 1) threshold:', milestone2Threshold, 'cents = $', (milestone2Threshold / 100));
      console.log('🎯 Quick Order List: Cart total (excluding freebie):', cartTotalExcludingFreeProduct, 'cents = $', (cartTotalExcludingFreeProduct / 100));
      
      // Milestone logic: Add free product at milestone2 threshold, remove below threshold
      if (cartTotalExcludingFreeProduct >= milestone2Threshold) {
        // Should have free product
        if (!hasFreeProduct) {
          console.log('Quick Order List: 🎉 MILESTONE $' + (milestone2Threshold / 100) + '+ REACHED! Adding free product');
          await this.addFreeProductToCart();
        } else {
          console.log('Quick Order List: ✅ MILESTONE $' + (milestone2Threshold / 100) + '+ REACHED! Free product already exists');
        }
      } else {
        // Should not have free product
        if (hasFreeProduct) {
          console.log('Quick Order List: 🚨 MILESTONE BELOW $' + (milestone2Threshold / 100) + '! Removing free product');
          await this.removeAllByVariantId(this.freeProductVariantId);
        } else {
          console.log('Quick Order List: ✅ MILESTONE BELOW $' + (milestone2Threshold / 100) + '! No free product to remove');
        }
      }
    }

    /**
     * Handles 'click' events on the cart items element.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      if (!evt.target.matches('.js-remove-item')) return;
      evt.preventDefault();
      this.updateQuantity(evt.target.dataset.index, 0, evt.target.dataset.variantId, evt.target);
    }

    /**
     * Handles 'change' events on the cart items element.
     * @param {object} evt - Event object.
     */
    handleChange(evt) {
      if (evt.target.dataset.index) {
        this.updateQuantity(
          evt.target.dataset.index,
          evt.target.value,
          evt.target.dataset.variantId,
          evt.target
        );
      } else {
        this.addToCart(evt.target.dataset.variantId, evt.target.value);
      }
    }

    async addToCart(variantId, quantity) {
      const requestData = {
        items: [
          {
            id: variantId,
            quantity
          }
        ],
        sections_url: window.location.pathname,
        sections: this.getSectionsToRender().map((section) => section.section)
      };

      let data;
      try {
        // Remove old errors
        this.querySelectorAll('.alert').forEach((alert) => alert.remove());
        this.classList.add('pointer-events-none');

        const response = await fetch(`${theme.routes.cartAdd}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify(requestData)
        });

        data = await response.json();
        if (!response.ok) throw new Error(response);

        // Refresh the entire cart if it's currently empty
        if (this.cartDrawer) this.cartDrawer.refreshCartDrawer();

        this.getSectionsToRender().forEach((section) => {
          const sectionEl = document.getElementById(section.id);
          if (!sectionEl) return;

          const el = sectionEl.querySelector(section.selector) || sectionEl;
          el.innerHTML = QuickOrderList
            .getElementHTML(data.sections[section.section], section.selector);
        });

        this.dispatchEvent(new CustomEvent('on:quick-order-list:update', {
          bubbles: true,
          detail: {
            productId: this.dataset.productId
          }
        }));

        // Handle free product milestone logic after adding to cart
        if (this.freeProductVariantId) {
          await this.handleFreeProductMilestone(data);
        }
      } catch (error) {
        console.error(error); // eslint-disable-line

        // Show errors
        const item = this.querySelector(`[data-variant-id="${variantId}"] .cart-item__details`);
        const msg = document.createElement('div');
        msg.classList.add('alert', 'mt-6', 'bg-error-bg', 'text-error-text');
        msg.textContent = data.description ? data.description : error;
        item.appendChild(msg);
      } finally {
        this.classList.remove('pointer-events-none');
      }
    }

    /**
     * Updates the quantity of a line item.
     * @param {number} line - Line item index.
     * @param {number} quantity - Quantity to set.
     * @param {string} variantId - Variant id being updated.
     * @param {object} quantityElem - The quantity input.
     */
    async updateQuantity(line, quantity, variantId, quantityElem) {
      this.enableLoading();

      const sectionsToRender = this.getSectionsToRender();

      if (this.cartDrawer) {
        const cartDrawerId = this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '');
        sectionsToRender.push({
          id: 'cart-drawer',
          section: cartDrawerId,
          selector: '#cart-drawer'
        });
      }

      this.fetchRequestOpts.body = JSON.stringify({
        line,
        quantity,
        sections: sectionsToRender.map((section) => section.section),
        sections_url: window.location.pathname
      });

      let data;

      try {
        // Remove old errors
        this.querySelectorAll('.alert').forEach((alert) => alert.remove());

        const response = await fetch(`${theme.routes.cartChange}`, this.fetchRequestOpts);
        data = await response.json();
        if (!response.ok) throw new Error(response);

        sectionsToRender.forEach((section) => {
          const sectionEl = document.getElementById(section.id);
          if (!sectionEl) return;

          const el = sectionEl.querySelector(section.selector) || sectionEl;
          el.innerHTML = QuickOrderList
            .getElementHTML(data.sections[section.section], section.selector);
        });

        this.dispatchEvent(new CustomEvent('on:quick-order-list:update', {
          bubbles: true,
          detail: {
            productId: this.dataset.productId
          }
        }));

        // Handle free product milestone logic after quantity change
        if (this.freeProductVariantId) {
          await this.handleFreeProductMilestone(data);
        }

        if (!this.closest('quick-add-drawer')) {
          this.disableLoading();
        }
      } catch (error) {
        console.log(error); // eslint-disable-line

        // Show errors
        const item = this.querySelector(`[data-variant-id="${variantId}"] .cart-item__qty`);
        const msg = document.createElement('div');
        msg.classList.add('alert', 'mt-6', 'bg-error-bg', 'text-error-text');
        msg.textContent = data.errors ? data.errors : error;
        item.appendChild(msg);

        // Update the quantity element
        quantityElem.value = quantityElem.dataset.initialValue;
        quantityElem.closest('quantity-input').currentQty = quantityElem.dataset.initialValue;

        this.disableLoading();
      }
    }

    /**
     * Returns an array of objects containing required section details.
     * @returns {Array}
     */
    getSectionsToRender() {
      return [
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        },
        {
          id: `quick-order-list_${this.dataset.section}`,
          section: this.dataset.section,
          selector: `#quick-order-list_${this.dataset.section}`
        },
        {
          id: 'free-shipping-notice',
          section: 'cart-drawer',
          selector: '.free-shipping-notice'
        }
      ];
    }

    /**
     * Gets the innerHTML of an element.
     * @param {string} html - Section HTML.
     * @param {string} selector - CSS selector for the element to get the innerHTML of.
     * @returns {string}
     */
    static getElementHTML(html, selector) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;

      const el = tmpl.content.querySelector(selector);
      return el ? el.innerHTML : '';
    }

    /**
     * Shows a loading icon over a line item.
     */
    enableLoading() {
      this.classList.add('pointer-events-none');
      document.activeElement.blur();
      this.itemStatus.setAttribute('aria-hidden', 'false');
    }

    /**
     * Stops the loading state
     */
    disableLoading() {
      this.classList.remove('pointer-events-none');
    }
  }

  customElements.define('quick-order-list', QuickOrderList);
}
